import { formatISO, startOfDay } from "date-fns";
import { getSupabaseClient } from "./supabaseClient";

const normalizeCategories = (categories = []) => {
    const seenWords = new Set();

    return categories.map((item, idx) => {
        // Deduplicate words within this category
        const uniqueWords = (item.words ?? []).filter((word) => {
            if (seenWords.has(word)) {
                console.warn(`Duplicate word found in puzzle data: "${word}"`);
                return false;
            }
            seenWords.add(word);
            return true;
        });

        return {
            category: item.category ?? `Group ${idx + 1}`,
            words: uniqueWords,
            difficulty: item.difficulty ?? idx + 1,
            imageSrc: item.imageSrc ?? null,
        };
    });
};

export const loadPuzzleForDate = async ({ sport, puzzleDate }) => {
    const normalizedDate = formatISO(startOfDay(puzzleDate), {
        representation: "date",
    });
    const supabase = getSupabaseClient();

    // If no Supabase connection, indicate no puzzle available
    if (!supabase) {
        return {
            gameData: [],
            puzzleId: null,
            puzzleDate: normalizedDate,
            sequence: null,
            source: "none",
            noPuzzle: true,
        };
    }

    const { data, error } = await supabase
        .from("puzzles")
        .select("id, league, puzzle_date, categories, category_1, category_2, category_3, category_4, sequence")
        .eq("league", sport)
        .eq("puzzle_date", normalizedDate)
        .maybeSingle();

    if (error) {
        console.error("Error loading puzzle:", error.message);
        return {
            gameData: [],
            puzzleId: null,
            puzzleDate: normalizedDate,
            sequence: null,
            source: "error",
            noPuzzle: true,
            error,
        };
    }

    // No puzzle exists for this exact date
    if (!data) {
        return {
            gameData: [],
            puzzleId: null,
            puzzleDate: normalizedDate,
            sequence: null,
            source: "none",
            noPuzzle: true,
        };
    }

    // Support both old (categories array) and new (individual category columns) schema
    let categories;
    if (data.category_1 || data.category_2 || data.category_3 || data.category_4) {
        // New schema: individual category columns
        categories = [data.category_1, data.category_2, data.category_3, data.category_4].filter(Boolean);
    } else if (Array.isArray(data.categories)) {
        // Old schema: categories array
        categories = data.categories;
    } else {
        // No valid categories found
        return {
            gameData: [],
            puzzleId: null,
            puzzleDate: normalizedDate,
            sequence: null,
            source: "none",
            noPuzzle: true,
        };
    }

    return {
        gameData: normalizeCategories(categories),
        puzzleId: data.id,
        puzzleDate: data.puzzle_date ?? normalizedDate,
        sequence: data.sequence ?? null,
        source: "supabase",
        noPuzzle: false,
    };
};
